import React, { useEffect, useState } from "react"
import axios from "axios"
import { Share2, Search, Megaphone, AlertTriangle, Lightbulb } from "lucide-react"
import { ReportCard, ReportSectionHeader, ReportStatTile, ReportEmptyState, ReportSkeleton, ReportBadge } from "../components/ReportUI"
import { REPORT_COLORS } from "../components/reportTheme"
import { okara_api } from "../../../utils/variables"

const BASE = okara_api
const TERMINAL_STATUSES = ["completed", "failed", "error", "done"]

const authHeaders = () => {
  const token = localStorage.getItem("authToken")
  const headers = { "Content-Type": "application/json" }
  if (token) headers.Authorization = `Bearer ${token}`
  return headers
}

const pollJob = async (url, body) => {
  let res = await axios.post(url, body, { headers: authHeaders() })
  let attempts = 0
  while (res.data?.job_status && !TERMINAL_STATUSES.includes(res.data.job_status) && attempts < 20) {
    await new Promise(r => setTimeout(r, 4000))
    res = await axios.post(url, body, { headers: authHeaders() })
    attempts++
  }
  return res.data
}

const fmtFollowers = n => {
  if (n == null) return "—"
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return `${n}`
}

const positionTone = position => {
  const p = (position || "").toLowerCase()
  if (p.includes("leader")) return "good"
  if (p.includes("niche")) return "warning"
  return "neutral"
}

export default function SocialComparisonSummaryTab({ appId, appName }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [accounts, setAccounts] = useState(null)
  const [seo, setSeo] = useState(null)
  const [ads, setAds] = useState(null)
  const [marketing, setMarketing] = useState(null)

  useEffect(() => {
    if (!appId) return
    let cancelled = false
    setLoading(true)
    setError(null)

    const safely = p => p.catch(err => {
      console.error("Social Comparison summary fetch failed:", err)
      return null
    })

    Promise.all([
      safely(axios.post(`${BASE}/socials/social-accounts/`, { app_id: appId }, { headers: authHeaders() }).then(r => r.data)),
      safely(pollJob(`${BASE}/socials/seo/`, { item_id: appId })),
      safely(pollJob(`${BASE}/socials/ads/`, { item_id: appId })),
      safely(pollJob(`${BASE}/socials/marketing-intelligence/`, { app_id: appId })),
    ]).then(([accountsRes, seoRes, adsRes, marketingRes]) => {
      if (cancelled) return
      setAccounts(accountsRes)
      setSeo(seoRes)
      setAds(adsRes)
      setMarketing(marketingRes)
      if (!accountsRes && !seoRes && !adsRes && !marketingRes) setError("Failed to load social comparison data")
    }).finally(() => !cancelled && setLoading(false))

    return () => {
      cancelled = true
    }
  }, [appId])

  if (!appId) return null
  if (loading) return <ReportSkeleton tiles={4} />
  if (error) return <ReportEmptyState icon={AlertTriangle} title="Failed to Load Social Comparison" description={error} />

  const topAccounts = (accounts?.accounts || [])
    .map(a => ({ ...a, followers: a.counts?.followers ?? a.counts?.subscribers ?? 0 }))
    .sort((a, b) => b.followers - a.followers)
    .slice(0, 4)

  return (
    <div>
      <ReportSectionHeader
        icon={Share2}
        eyebrow="Social Comparison"
        title={appName ? `${appName} — Social Presence` : "Social Presence"}
        action={marketing?.position ? <ReportBadge tone={positionTone(marketing.position)}>{marketing.position}</ReportBadge> : null}
      />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 16 }}>
        <ReportStatTile label="Total Followers" value={fmtFollowers(accounts?.summary?.total_followers)} sub={`${accounts?.summary?.accounts_count ?? 0} accounts`} />
        <ReportStatTile label="SEO Score" value={seo?.seo_score != null ? `${seo.seo_score}/${seo.max_score ?? 10}` : "—"} />
        <ReportStatTile label="Active Ads" value={ads?.ads?.active_ad_count ?? "—"} sub={`${ads?.ads?.ad_count ?? 0} total`} />
        <ReportStatTile label="Marketing Score" value={marketing?.score != null ? marketing.score : "—"} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12, marginBottom: 16 }}>
        <ReportCard>
          <div style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary, marginBottom: 12 }}>Top Platforms</div>
          {topAccounts.length === 0 ? (
            <div style={{ fontSize: 12, color: REPORT_COLORS.textMuted }}>No connected social accounts found.</div>
          ) : (
            topAccounts.map((a, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < topAccounts.length - 1 ? `1px solid ${REPORT_COLORS.cardBorder}` : "none" }}>
                <span style={{ fontSize: 13, color: REPORT_COLORS.textSecondary, textTransform: "capitalize" }}>{a.platform}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary, fontFamily: "monospace" }}>{fmtFollowers(a.followers)}</span>
              </div>
            ))
          )}
        </ReportCard>

        <ReportCard>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <Search size={14} color={REPORT_COLORS.textMuted} />
            <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>SEO Suggestions</span>
          </div>
          {(seo?.optimization_suggestions || []).length === 0 ? (
            <div style={{ fontSize: 12, color: REPORT_COLORS.textMuted }}>No SEO suggestions available.</div>
          ) : (
            seo.optimization_suggestions.slice(0, 3).map((s, i) => (
              <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 8, display: "flex", gap: 8 }}>
                <span style={{ color: REPORT_COLORS.accent, fontWeight: 700 }}>•</span>
                {s}
              </div>
            ))
          )}
        </ReportCard>
      </div>

      {marketing?.summary && (
        <ReportCard>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <Megaphone size={14} color={REPORT_COLORS.accent} />
            <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Marketing Intelligence</span>
          </div>
          <div style={{ fontSize: 13, color: REPORT_COLORS.textSecondary, marginBottom: marketing.actions?.length ? 12 : 0 }}>{marketing.summary}</div>
          {marketing.actions?.length > 0 && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, paddingTop: 10, borderTop: `1px solid ${REPORT_COLORS.cardBorder}` }}>
              <Lightbulb size={14} color={REPORT_COLORS.warning} style={{ marginTop: 1, flexShrink: 0 }} />
              <div style={{ fontSize: 12, color: REPORT_COLORS.textSecondary }}>
                <strong style={{ color: REPORT_COLORS.textPrimary }}>Top action: </strong>
                {marketing.actions[0].do}
              </div>
            </div>
          )}
        </ReportCard>
      )}
    </div>
  )
}
